import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { DeterministicNamingStepFollowUp, getDeterministicNamingStep, getNamingQuestionPlaceholder, getNamingQuestions, getNumericSlots, getOptionalSlots, getSlotOrder, NamingQuestionTranslations } from '@kheops/utils';
import { useSuggestProductNameLazyQuery } from '../../queries/__generated__/suggestProductName.generated';
import { useBuildProductNameLazyQuery } from '../../queries/__generated__/buildProductName.generated';

export interface NamingCandidate {
  displayValue: string;
  value: string;
  followUp?: DeterministicNamingStepFollowUp;
  followUpQuestion?: string;
}

export interface NamingWizardStep {
  slot: string;
  question: string;
  placeholder: string;
  candidates: NamingCandidate[];
  isNumeric: boolean;
  isOptional: boolean;
}

export type NamingWizardStatus = 'idle' | 'loading' | 'question' | 'building' | 'final';

export interface UseProductNameSuggestionWizardParams {
  name: string;
  subFamily?: Product_Sub_Family_Name_Enum | '';
  brand?: string | null;
  description?: string | null;
  onComplete: (finalName: string) => void;
  onError: (error: Error) => void;
}

export interface ProductNameSuggestionWizard {
  status: NamingWizardStatus;
  currentStep?: NamingWizardStep;
  pendingAgeRange?: NamingCandidate;
  progress: number;
  canGoBack: boolean;
  originalName: string;
  suggestedName: string;
  start: () => Promise<void>;
  selectCandidate: (candidate: NamingCandidate) => void;
  confirmAgeRange: (rangeLabel: string) => void;
  back: () => void;
  acceptSuggestion: (finalName: string) => void;
  keepOriginal: () => void;
}

export function hasNamingWizard(subFamily?: Product_Sub_Family_Name_Enum | ''): boolean {
  return !!subFamily && !!getNamingQuestions(subFamily);
}

export default function useProductNameSuggestionWizard({
  name,
  subFamily,
  brand,
  description,
  onComplete,
  onError,
}: UseProductNameSuggestionWizardParams): ProductNameSuggestionWizard {
  const { i18n } = useTranslation();
  const [fetchSuggestions] = useSuggestProductNameLazyQuery({ fetchPolicy: 'cache-first' });
  const [fetchBuiltName] = useBuildProductNameLazyQuery({ fetchPolicy: 'cache-first' });
  const [status, setStatus] = useState<NamingWizardStatus>('idle');
  const [steps, setSteps] = useState<NamingWizardStep[]>([]);
  const [answers, setAnswers] = useState<Record<string, NamingCandidate>>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [pendingAgeRange, setPendingAgeRange] = useState<NamingCandidate>();
  const [originalName, setOriginalName] = useState('');
  const [suggestedName, setSuggestedName] = useState('');

  const currentStep = steps[stepIndex];

  const buildSteps = useCallback((
    currentSubFamily: Product_Sub_Family_Name_Enum,
    suggestions: { slot: string; suggestions: string[] }[],
  ): NamingWizardStep[] => {
    const questions = new Map((getNamingQuestions(currentSubFamily) ?? []).map(({ slot, question }) => [slot, question]));
    const numericSlots = new Set(getNumericSlots(currentSubFamily));
    const optionalSlots = new Set(getOptionalSlots(currentSubFamily));
    const suggestionsBySlot = new Map(suggestions.map((suggestion) => [suggestion.slot, suggestion.suggestions]));
    const translate = (translations?: NamingQuestionTranslations): string => translations?.[i18n.resolvedLanguage ?? ''] ?? '';

    return (getSlotOrder(currentSubFamily) ?? []).flatMap((slot): NamingWizardStep[] => {
      const deterministicStep = getDeterministicNamingStep(slot);

      if (deterministicStep) {
        return [{
          slot,
          question: translate(deterministicStep.question),
          placeholder: translate(getNamingQuestionPlaceholder(slot)),
          candidates: deterministicStep.options.map(({ label, appendsLabel, followUp, followUpQuestion }) => {
            const displayValue = translate(label);

            return { displayValue, value: appendsLabel ? displayValue : '', followUp, followUpQuestion: translate(followUpQuestion) };
          }),
          isNumeric: false,
          isOptional: optionalSlots.has(slot),
        }];
      }

      const question = questions.get(slot);

      if (!question) {
        return [];
      }

      return [{
        slot,
        question: translate(question),
        placeholder: translate(getNamingQuestionPlaceholder(slot)),
        candidates: (suggestionsBySlot.get(slot) ?? []).map((suggestion) => ({ displayValue: suggestion, value: suggestion })),
        isNumeric: numericSlots.has(slot),
        isOptional: optionalSlots.has(slot),
      }];
    });
  }, [i18n.resolvedLanguage]);

  const start = useCallback(async (): Promise<void> => {
    if (!subFamily) {
      return;
    }

    setStatus('loading');

    try {
      const { data, error } = await fetchSuggestions({
        variables: {
          name,
          subFamily,
          brand: brand || undefined,
          description: description || undefined,
        },
      });

      if (error || !data?.suggestProductName.length) {
        throw error ?? new Error('Empty product name suggestions');
      }

      setSteps(buildSteps(subFamily, data.suggestProductName));
      setOriginalName(name);
      setAnswers({});
      setStepIndex(0);
      setPendingAgeRange(undefined);
      setStatus('question');
    } catch (error) {
      setStatus('idle');
      onError(error as Error);
    }
  }, [name, subFamily, brand, description, fetchSuggestions, buildSteps, onError]);

  // The name is written by the naming engine from the answers, skipped slots (value '') are not sent
  const buildName = useCallback(async (finalAnswers: Record<string, NamingCandidate>): Promise<void> => {
    if (!subFamily) {
      return;
    }

    setStatus('building');

    try {
      const { data, error } = await fetchBuiltName({
        variables: {
          subFamily,
          answers: Object.entries(finalAnswers)
            .filter(([, { value }]) => value)
            .map(([slot, { value }]) => ({ slot, value })),
        },
      });

      if (error || !data?.buildProductName.name) {
        throw error ?? new Error('Empty built product name');
      }

      setSuggestedName(data.buildProductName.name);
      setStatus('final');
    } catch (error) {
      setStatus('question');
      onError(error as Error);
    }
  }, [subFamily, fetchBuiltName, onError]);

  const answerCurrentStep = useCallback((candidate: NamingCandidate): void => {
    const nextAnswers = { ...answers, [currentStep.slot]: candidate };

    setAnswers(nextAnswers);
    setPendingAgeRange(undefined);

    if (stepIndex + 1 < steps.length) {
      setStepIndex(stepIndex + 1);
    } else {
      buildName(nextAnswers);
    }
  }, [answers, currentStep, stepIndex, steps.length, buildName]);

  const selectCandidate = useCallback((candidate: NamingCandidate): void => {
    if (candidate.followUp === 'ageRange') {
      setPendingAgeRange(candidate);
      return;
    }

    answerCurrentStep(candidate);
  }, [answerCurrentStep]);

  const confirmAgeRange = useCallback((rangeLabel: string): void => {
    if (!pendingAgeRange) {
      return;
    }

    answerCurrentStep({ ...pendingAgeRange, displayValue: rangeLabel, value: rangeLabel });
  }, [pendingAgeRange, answerCurrentStep]);

  const back = useCallback((): void => {
    if (pendingAgeRange) {
      setPendingAgeRange(undefined);
      return;
    }

    if (status === 'final') {
      setStatus('question');
      setStepIndex(steps.length - 1);
    } else if (stepIndex === 0) {
      setStatus('idle');
    } else {
      setStepIndex(stepIndex - 1);
    }
  }, [status, stepIndex, steps.length, pendingAgeRange]);

  const acceptSuggestion = useCallback((finalName: string): void => {
    setStatus('idle');
    onComplete(finalName.trim() || originalName);
  }, [originalName, onComplete]);

  const keepOriginal = useCallback((): void => {
    setStatus('idle');
    onComplete(originalName);
  }, [originalName, onComplete]);

  return {
    status,
    currentStep,
    pendingAgeRange,
    progress: status === 'final' || status === 'building' ? 1 : (stepIndex + 1) / (steps.length + 1),
    canGoBack: status === 'final' || ((status === 'question' || status === 'building') && stepIndex > 0),
    originalName,
    suggestedName,
    start,
    selectCandidate,
    confirmAgeRange,
    back,
    acceptSuggestion,
    keepOriginal,
  };
}
